import React, { useState } from "react";
import axios from "axios";
import { Phone, Mail, MapPin, Clock, Send, ArrowUpRight, CheckCircle2 } from "lucide-react";
import PageBanner from "../components/PageBanner";
import Reveal from "../components/Reveal";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { useToast } from "../hooks/use-toast";
import { contactInfo, BOOK_MEETING_URL } from "../mock";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Capture UTM / source data so funnel attribution works later.
const getSource = () => {
  const p = new URLSearchParams(window.location.search);
  return {
    utm_source: p.get("utm_source") || "",
    utm_medium: p.get("utm_medium") || "",
    utm_campaign: p.get("utm_campaign") || "",
    utm_term: p.get("utm_term") || "",
    utm_content: p.get("utm_content") || "",
    referrer: document.referrer || "",
    path: window.location.pathname,
  };
};

const ContactPage = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast({ title: "Please complete the required fields", variant: "destructive" });
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/enquiries`, { ...form, source: getSource() });
      setSubmitted(true);
      setForm({ name: "", email: "", company: "", message: "" });
    } catch (err) {
      toast({
        title: "Something went wrong",
        description: "Please try again, or email us directly at " + contactInfo.email,
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const details = [
    { Icon: Phone, label: "Phone", value: contactInfo.phone, href: `tel:${contactInfo.phoneRaw}` },
    { Icon: Mail, label: "Email", value: contactInfo.email, href: `mailto:${contactInfo.email}` },
    { Icon: Clock, label: "Working Hours", value: contactInfo.hours },
    ...contactInfo.offices.map((o) => ({
      Icon: MapPin,
      label: `${o.label} · ${o.city}`,
      value: o.address,
    })),
  ];

  return (
    <>
      <PageBanner
        title="Let’s Connect"
        subtitle="Tell us about your expansion goals — whether entering India, expanding globally, or building a trade programme."
        crumbs={[{ label: "Contact" }]}
      />
      <section className="bg-white py-20 md:py-24">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-12 px-4 md:px-8 lg:grid-cols-5">
          {/* Info */}
          <Reveal className="lg:col-span-2">
            <h2 className="text-2xl font-semibold text-[#0F2A4A]">Get in touch</h2>
            <p className="mt-3 text-slate-600">
              We act as an extension of your leadership team, executing your
              vision for expansion.
            </p>
            <div className="mt-8 space-y-5">
              {details.map((d) => (
                <div key={d.label} className="flex items-start gap-4">
                  <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-[#0F2A4A]/5 text-[#0F2A4A]">
                    <d.Icon className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">
                      {d.label}
                    </div>
                    {d.href ? (
                      <a href={d.href} className="text-[#0F2A4A] hover:text-[#E4545F]">
                        {d.value}
                      </a>
                    ) : (
                      <div className="text-[#0F2A4A]">{d.value}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <a
              href={BOOK_MEETING_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#0F2A4A] px-7 py-3 text-sm font-semibold text-white transition-all duration-200 hover:-translate-y-0.5 hover:bg-[#0b2038]"
            >
              Book a Meeting <ArrowUpRight className="h-4 w-4" />
            </a>
          </Reveal>

          {/* Form */}
          <Reveal delay={120} className="lg:col-span-3">
            {submitted ? (
              <div className="flex h-full flex-col items-center justify-center rounded-2xl border border-slate-100 bg-white p-10 text-center shadow-[0_20px_60px_-30px_rgba(15,42,74,0.4)]">
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-[#E4545F]/10 text-[#E4545F]">
                  <CheckCircle2 className="h-8 w-8" />
                </div>
                <h3 className="mt-6 text-2xl font-semibold text-[#0F2A4A]">
                  Thank you — your enquiry is in.
                </h3>
                <p className="mt-3 max-w-md text-slate-600">
                  We’ve received your message and our team will be in touch shortly.
                  For anything urgent, email us at {contactInfo.email}.
                </p>
                <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                  <a
                    href={BOOK_MEETING_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 rounded-full bg-[#0F2A4A] px-7 py-3 text-sm font-semibold text-white transition-all duration-200 hover:-translate-y-0.5 hover:bg-[#0b2038]"
                  >
                    Book a Meeting <ArrowUpRight className="h-4 w-4" />
                  </a>
                  <button
                    onClick={() => setSubmitted(false)}
                    className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-7 py-3 text-sm font-semibold text-[#0F2A4A] transition-colors hover:border-slate-300"
                  >
                    Send another message
                  </button>
                </div>
              </div>
            ) : (
              <form
                onSubmit={onSubmit}
                className="rounded-2xl border border-slate-100 bg-white p-8 shadow-[0_20px_60px_-30px_rgba(15,42,74,0.4)]"
              >
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                  <div>
                    <Label htmlFor="name">Name *</Label>
                    <Input id="name" value={form.name} onChange={update("name")} placeholder="Your name" className="mt-2" />
                  </div>
                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input id="email" type="email" value={form.email} onChange={update("email")} placeholder="you@company.com" className="mt-2" />
                  </div>
                </div>
                <div className="mt-5">
                  <Label htmlFor="company">Company</Label>
                  <Input id="company" value={form.company} onChange={update("company")} placeholder="Company name" className="mt-2" />
                </div>
                <div className="mt-5">
                  <Label htmlFor="message">Message *</Label>
                  <Textarea id="message" value={form.message} onChange={update("message")} placeholder="How can we help?" rows={5} className="mt-2" />
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#E4545F] px-8 py-3.5 text-sm font-semibold text-white transition-all duration-200 hover:-translate-y-0.5 hover:bg-[#C9434E] disabled:opacity-60"
                >
                  {submitting ? "Sending..." : "Send Message"} <Send className="h-4 w-4" />
                </button>
              </form>
            )}
          </Reveal>
        </div>
      </section>
    </>
  );
};

export default ContactPage;
